import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";

import {
  UpdateSubscriberSchema,
  UpdateSubscriberSchemaType,
} from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  AudienceContactUpdateSource,
  GetAudienceContactFieldsQuery,
  SoftDeleteAudienceContactMutation,
  SoftDeleteAudienceContactMutationVariables,
  UndeleteAudienceContactMutation,
  UndeleteAudienceContactMutationVariables,
  UpdateAudienceContactMutation,
  UpdateAudienceContactMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { RhfErrorBanner } from "~frontend/components/forms/react-hook-form-components";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import {
  GET_AUDIENCE_SUMMARY,
  LIST_AUDIENCE_SEARCH_RESULTS,
  SOFT_DELETE_AUDIENCE_CONTACT,
  UNDELETE_AUDIENCE_CONTACT,
  UPDATE_AUDIENCE_CONTACT,
} from "~frontend/graphql/audience";
import { AudienceContact } from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import { createUpdateSubscriberInitialValues } from "~frontend/routes/subaccounts/audiences/Subscribers/utils";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { ContactFieldsEditor } from "./ContactFieldsEditor";

const AudienceContactDialogActions = styled(DialogActions)({
  justifyContent: "space-between",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginBottom: theme.spacing(1),
});

type ContactHistoryRecord = { id: string; label: string; timestamp: number };

function AudienceContactDetails({
  audienceContact,
  editing = false,
  control,
  fieldDefinitions,
  showSaveSuccessful,
}: {
  audienceContact: AudienceContact;
  editing?: boolean;
  control: any;
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  showSaveSuccessful: boolean;
}) {
  const contactHistory: ContactHistoryRecord[] = audienceContact.updates.reduce(
    (acc, acu) => {
      let label = "Updated";

      switch (acu.source) {
        case AudienceContactUpdateSource.BulkUpload: {
          label =
            acu.sourceDetails.uploadFileName && acu.sourceDetails.uploadedBy
              ? `Bulk upload from ${acu.sourceDetails.uploadFileName} by ${acu.sourceDetails.uploadedBy}`
              : "Bulk upload";
          break;
        }
        case AudienceContactUpdateSource.AdminManual: {
          label =
            acu.sourceDetails.action === "updatedBy"
              ? `Details updated by ${acu.sourceDetails.userFullName}`
              : acu.sourceDetails.action === "deletedBy"
                ? `Removed from audience by ${acu.sourceDetails.userFullName}`
                : acu.sourceDetails.action === "undeletedBy"
                  ? `Manually added back by ${acu.sourceDetails.userFullName}`
                  : "Details manually updated";
          break;
        }
        case AudienceContactUpdateSource.Api: {
          label = acu.sourceDetails.apiKeyName
            ? `Updated by API Key ${acu.sourceDetails.apiKeyName}`
            : "Updated by API";
          break;
        }

        case AudienceContactUpdateSource.Actblue: {
          label = "ActBlue donation";
          break;
        }

        case AudienceContactUpdateSource.ActionKit: {
          label = acu.sourceDetails.actionKitPageName
            ? `ActionKit form submission from ${acu.sourceDetails.actionKitPageName}`
            : "ActionKit form submission";
          break;
        }

        case AudienceContactUpdateSource.Keyword: {
          label = acu.sourceDetails.keyword
            ? `Sent keyword ${acu.sourceDetails.keyword}`
            : "Sent keyword";
          break;
        }

        case AudienceContactUpdateSource.LegacyShortCodeSubscriber: {
          label = "Bulk uploaded";
          break;
        }
      }

      acc.push({
        label,
        timestamp: acu.createdAt,
        id: acu.id,
      });
      return acc;
    },
    [] as ContactHistoryRecord[]
  );

  return (
    <FlexBox direction="column" gap={3} data-testid="audience-contact-details">
      <ContactFieldsEditor
        fields={audienceContact.fields}
        editing={editing}
        control={control}
        fieldDefinitions={fieldDefinitions}
        showSaveSuccessful={showSaveSuccessful}
      />

      <Accordion disableGutters sx={{ padding: 0, background: "none" }}>
        <AccordionSummary
          expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">Contact History</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
          }}
        >
          <List sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
            {contactHistory.map(ch => (
              <ListItem
                sx={{
                  px: 0,
                }}
                key={`contact-history-${ch.id}`}
              >
                <ListItemText
                  primary={<Typography variant="body2">{ch.label}</Typography>}
                  secondary={
                    <Typography variant="body3">
                      {formatShortDateTime(ch.timestamp)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    </FlexBox>
  );
}

export function ArchivedAudienceContactDetails({
  audienceContact,
  audienceContactFields,
  onClose,
}: {
  audienceContact: AudienceContact;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const initialValues = createUpdateSubscriberInitialValues(
    audienceContact.id,
    audienceContact.fields,
    audienceContactFields
  );

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    values: initialValues,
  });

  const { control } = methods;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testId="edit-audience-contact-details-dialog"
    >
      <DialogTitle sx={{ px: 5 }}>
        Contact Details for{" "}
        <strong>{prettyPhoneNumber(audienceContact.phoneNumber)}</strong>
      </DialogTitle>
      <DialogContent sx={{ px: 5 }}>
        <AudienceContactDetails
          audienceContact={audienceContact}
          editing={false}
          control={control}
          fieldDefinitions={audienceContactFields}
          showSaveSuccessful={false}
        />
      </DialogContent>
      <AudienceContactDialogActions sx={{ px: 5 }}>
        <Button onClick={onClose} variant="contained" color="tertiary">
          Close
        </Button>
      </AudienceContactDialogActions>
    </Dialog>
  );
}

export function AudienceContactDetailsDialog({
  audienceContact,
  updateAudienceContact,
  audienceId,
  audienceContactFields,
  onClose,
}: {
  audienceContact: AudienceContact;
  updateAudienceContact: () => void;
  audienceId: string;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const formatErrorMessage = useErrorMessageFormatter();
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [undeletionLoading, setUndeletionLoading] = useState(false);
  const [showSaveSuccessful, setShowSaveSuccessful] = useState(false);

  const [editing, setEditing] = useState(false);
  const [dialogPage, setDialogPage] = useState<"details" | "remove" | "add">(
    "details"
  );

  const snackbar = useSnackbar();
  const initialValues = UpdateSubscriberSchema(audienceContactFields).parse({
    subscriberId: audienceContact.id,
    fields: (audienceContact.fields || []).reduce(
      (acc, field) => {
        if (field.type === AudienceContactFieldType.Boolean) {
          acc[field.name] = field.value != null ? field.value : null;
        } else {
          acc[field.name] = field.value || null;
        }
        return acc;
      },
      {} as Record<string, string | boolean | null>
    ),
  });

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: initialValues,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [updateAudienceContactMutation] = useMutation<
    UpdateAudienceContactMutation,
    UpdateAudienceContactMutationVariables
  >(UPDATE_AUDIENCE_CONTACT, {
    refetchQueries: [GET_AUDIENCE_SUMMARY, LIST_AUDIENCE_SEARCH_RESULTS],
    awaitRefetchQueries: true,
  });

  const [softDeleteAudienceContactMutation] = useMutation<
    SoftDeleteAudienceContactMutation,
    SoftDeleteAudienceContactMutationVariables
  >(SOFT_DELETE_AUDIENCE_CONTACT, {
    refetchQueries: [GET_AUDIENCE_SUMMARY, LIST_AUDIENCE_SEARCH_RESULTS],
    awaitRefetchQueries: true,
  });

  const [undeleteAudienceContact] = useMutation<
    UndeleteAudienceContactMutation,
    UndeleteAudienceContactMutationVariables
  >(UNDELETE_AUDIENCE_CONTACT, {
    refetchQueries: [GET_AUDIENCE_SUMMARY, LIST_AUDIENCE_SEARCH_RESULTS],
    awaitRefetchQueries: true,
  });

  async function handleUpdateSubmit(rawValues: unknown) {
    const values = UpdateSubscriberSchema(audienceContactFields).parse(
      rawValues
    );
    setMutationLoading(true);
    setMutationError(null);

    const { errors } = await updateAudienceContactMutation({
      variables: {
        input: {
          audienceId,
          audienceContactId: audienceContact.id,
          fields: JSON.stringify(values.fields),
        },
      },
    });

    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      updateAudienceContact();
      setEditing(false);
      setShowSaveSuccessful(true);
    }

    setMutationLoading(false);
  }

  async function handleContactDelete() {
    setDeletionLoading(true);
    setDeletionError(null);
    const { errors } = await softDeleteAudienceContactMutation({
      variables: {
        input: {
          audienceId,
          audienceContactId: audienceContact.id,
        },
      },
    });

    if (errors) {
      setDeletionError(formatErrorMessage(errors));
    } else {
      onClose();
    }

    setDeletionLoading(false);
  }

  async function handleContactUndelete() {
    setUndeletionLoading(true);

    const { errors } = await undeleteAudienceContact({
      variables: {
        input: {
          audienceId,
          audienceContactId: audienceContact.id,
        },
      },
    });

    if (errors) {
      snackbar.enqueueSnackbar(formatErrorMessage(errors), {
        variant: "error",
      });
    } else {
      onClose();
    }

    setUndeletionLoading(false);
  }

  function renderDetails() {
    return (
      <>
        <DialogTitle sx={{ px: 5 }}>
          Contact Details for{" "}
          <strong>{prettyPhoneNumber(audienceContact.phoneNumber)}</strong>
        </DialogTitle>
        <DialogContent sx={{ px: 5 }}>
          {editing && <RhfErrorBanner control={control} />}
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          {audienceContact.deletedAt && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This contact has been deleted from this audience. You can add them
              back using the button below.
            </Alert>
          )}
          <AudienceContactDetails
            audienceContact={audienceContact}
            editing={editing}
            control={control}
            fieldDefinitions={audienceContactFields}
            showSaveSuccessful={showSaveSuccessful}
          />
        </DialogContent>
        <AudienceContactDialogActions sx={{ px: 5 }}>
          <FlexBox>
            {audienceContact.deletedAt ? (
              <Button
                onClick={() => handleContactUndelete()}
                variant="contained"
                color="tertiary"
                disabled={undeletionLoading}
              >
                {undeletionLoading ? "Adding..." : "Add to Audience"}
              </Button>
            ) : (
              <Button
                onClick={() => setDialogPage("remove")}
                variant="contained"
                color="tertiary"
              >
                Remove from Audience
              </Button>
            )}
          </FlexBox>
          <FlexBox gap={1}>
            <Button onClick={onClose} variant="contained" color="tertiary">
              Close
            </Button>
            {audienceContact.deletedAt ? null : editing ? (
              <Button
                onClick={handleSubmit(handleUpdateSubmit)}
                color="primary"
                variant="contained"
                disabled={isSubmitting || mutationLoading}
              >
                {isSubmitting || mutationLoading ? "Saving..." : "Save"}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setShowSaveSuccessful(false);
                  setEditing(true);
                }}
                color="primary"
                variant="contained"
              >
                Edit
              </Button>
            )}
          </FlexBox>
        </AudienceContactDialogActions>
      </>
    );
  }

  function renderRemove() {
    return (
      <>
        <DialogTitle sx={{ px: 5 }}>
          Remove{" "}
          <strong>{prettyPhoneNumber(audienceContact.phoneNumber)}</strong> from
          Audience?
        </DialogTitle>
        <DialogContent sx={{ px: 5 }}>
          {deletionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deletionError}
            </Alert>
          )}
          If removed, this contact will not be included in future messages to
          this audience. The existing thread with this contact will remain
          intact, and if the contact messages on this thread admins and texters
          can still reply from the inbox or threads page.
        </DialogContent>
        <AudienceContactDialogActions
          sx={{ px: 5, justifyContent: "flex-end" }}
        >
          <Button
            onClick={() => {
              setDialogPage("details");
            }}
            color="tertiary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleContactDelete()}
            color="primary"
            variant="contained"
            disabled={deletionLoading}
          >
            {deletionLoading ? "Removing..." : "Remove from Audience"}
          </Button>
        </AudienceContactDialogActions>
      </>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testId="edit-audience-contact-details-dialog"
    >
      {dialogPage === "details" && renderDetails()}
      {dialogPage === "remove" && renderRemove()}
    </Dialog>
  );
}
